import { useState } from "react";
import {
  Button,
  Tooltip,
  TooltipTrigger,
  ToastQueue,
  Typography,
} from "@repo/design-system";
import type {
  IMoleculeExternalDBItem,
  IMoleculeExternalDBsSection,
} from "@/api/moleculeDetails";
import { streamDownloadGet } from "../helpers";

interface IExternalDBsCardProps {
  section: IMoleculeExternalDBsSection;
  smiles: string | null;
}

// Keyed by displayName since that's the only stable identifier the API gives
// per external-DB row — BindingDB's assay lookup is by SMILES, the rest by id.
const DOWNLOAD_ENDPOINTS: Record<
  string,
  {
    buildPath: (value: string, smiles: string | null) => string;
    defaultFilename: string;
  }
> = {
  "PubChem ID": {
    buildPath: (value) => `assay/pubchem/${encodeURIComponent(value)}/download`,
    defaultFilename: "pubchem-assay.csv",
  },
  "ChEMBL ID": {
    buildPath: (value) => `assay/chembl/${encodeURIComponent(value)}/download`,
    defaultFilename: "chembl-assay.csv",
  },
  "BindingDB ID": {
    buildPath: (_value, smiles) =>
      `assay/bdb/${encodeURIComponent(smiles ?? "")}/download`,
    defaultFilename: "bdb-assay.csv",
  },
};

// Always render these three rows in this order, regardless of whether the
// API includes an unavailable DB with a null value or omits it outright —
// this table stays a fixed 3-row layout either way.
const EXTERNAL_DB_ORDER = ["PubChem ID", "ChEMBL ID", "BindingDB ID"];

export function ExternalDBsCard({ section, smiles }: IExternalDBsCardProps) {
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});

  const rows: IMoleculeExternalDBItem[] = EXTERNAL_DB_ORDER.map(
    (displayName) =>
      section.items.find((item) => item.displayName === displayName) ?? {
        displayName,
        value: null,
        url: null,
      },
  );

  const handleDownload = async (item: IMoleculeExternalDBItem) => {
    const config = DOWNLOAD_ENDPOINTS[item.displayName];
    if (!config || !item.value || downloading[item.displayName]) return;

    setDownloading((prev) => ({ ...prev, [item.displayName]: true }));
    try {
      await streamDownloadGet(
        config.buildPath(item.value, smiles),
        config.defaultFilename,
      );
    } catch {
      ToastQueue.danger("Download failed. Please try again.");
    } finally {
      setDownloading((prev) => ({ ...prev, [item.displayName]: false }));
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Typography
          variant="paragraph-l-semibold"
          className="text-contentPrimary">
          {section.displayName}
        </Typography>
        <TooltipTrigger delay={0}>
          <Button variant="transparent" size="small" leftIcon="info-circle" />
          <Tooltip>
            Download is performed by using public APIs of listed sources
          </Tooltip>
        </TooltipTrigger>
      </div>
      <div className="overflow-hidden rounded-xl border border-borderPrimary bg-backgroundPrimary">
        <div className="grid grid-cols-[1fr_1fr_min-content] gap-6 border-b border-borderPrimary bg-backgroundSecondary px-5 py-3">
          <Typography variant="paragraph-m-semibold" className="text-b700">
            Database
          </Typography>
          <Typography variant="paragraph-m-semibold" className="text-b700">
            ID
          </Typography>
          <div />
        </div>
        {rows.map((item, idx) => (
          <div
            key={item.displayName}
            className={`grid grid-cols-[1fr_1fr_min-content] items-center gap-6 px-5 py-3 ${
              idx < rows.length - 1 ? "border-b border-borderPrimary" : ""
            }`}>
            <Typography variant="paragraph-m-semibold" className="text-b700">
              {item.displayName}
            </Typography>
            {item.value && item.url ? (
              <>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-pointer">
                  <Typography
                    variant="paragraph-m-semibold"
                    className="text-contentBrand underline-offset-2 hover:underline">
                    {item.value}
                  </Typography>
                </a>
                <Button
                  variant="secondary"
                  size="small"
                  leftIcon={
                    downloading[item.displayName] ? undefined : "download"
                  }
                  loading={downloading[item.displayName]}
                  isDisabled={downloading[item.displayName]}
                  onPress={() => handleDownload(item)}
                />
              </>
            ) : (
              <>
                <Typography
                  variant="paragraph-m-regular"
                  className="text-contentSecondary">
                  Not Available
                </Typography>
                <div />
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
